import { useEffect } from "react";

const MODAL_SELECTOR = "[data-scroll-lock-modal]";

const SCROLLABLE_SELECTOR =
  ".campaign-window-scrollbar, main, [data-scroll-lock-target], .mint-space-window";

/**
 * Lock background scroll while a modal is open — including nested mint-space panes.
 */
export default function useLockBackgroundScroll(locked) {
  useEffect(() => {
    if (!locked || typeof document === "undefined") return undefined;

    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyPaddingRight: body.style.paddingRight,
    };

    html.classList.add("scroll-locked");
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    const touched = [];
    const lockEl = (el) => {
      if (!el || touched.some((entry) => entry.el === el)) return;
      touched.push({
        el,
        overflow: el.style.overflow,
        overscrollBehavior: el.style.overscrollBehavior,
      });
      el.style.overflow = "hidden";
      el.style.overscrollBehavior = "none";
    };

    document.querySelectorAll(SCROLLABLE_SELECTOR).forEach(lockEl);

    const allowScroll = (target) => {
      if (!(target instanceof Node)) return false;
      return Boolean(target.closest?.(MODAL_SELECTOR));
    };

    const blockScroll = (e) => {
      if (allowScroll(e.target)) return;
      e.preventDefault();
    };

    document.addEventListener("wheel", blockScroll, { passive: false, capture: true });
    document.addEventListener("touchmove", blockScroll, { passive: false, capture: true });

    return () => {
      document.removeEventListener("wheel", blockScroll, { capture: true });
      document.removeEventListener("touchmove", blockScroll, { capture: true });

      html.classList.remove("scroll-locked");
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.width = prev.bodyWidth;
      body.style.paddingRight = prev.bodyPaddingRight;
      window.scrollTo(0, scrollY);

      touched.forEach(({ el, overflow, overscrollBehavior }) => {
        el.style.overflow = overflow;
        el.style.overscrollBehavior = overscrollBehavior;
      });
    };
  }, [locked]);
}
